import React, { useEffect, useState } from "react";

import { Button, CircularProgress } from "@mui/material";
import { SubmissionSummary } from "@rubi/api-generated";
import { colors, useMapColorScheme } from "@rubi/ui";
import { GoogleMapsProvider } from "@rubi/ui";
import { AdvancedMarker, Map, Pin } from "@vis.gl/react-google-maps";

interface ReportGeometry {
  lat?: number;
  lng?: number;
}

interface ReportLocation {
  googleMapsPlace?: {
    geometry?: ReportGeometry;
  } | null;
}

const getReportGeometry = (
  report: SubmissionSummary,
): ReportGeometry | undefined => {
  const location = report.location as ReportLocation | null | undefined;
  return location?.googleMapsPlace?.geometry;
};

interface ReportsMapProps {
  reports: SubmissionSummary[];
  reportClickHandler: (report: SubmissionSummary, index: number) => void;
  loadMore: () => void;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  totalReports: number;
  selectedReport?: SubmissionSummary;
  setSelectedReport: (report: SubmissionSummary) => void;
}

const ReportsMap: React.FC<ReportsMapProps> = ({
  reports,
  reportClickHandler,
  loadMore,
  isFetchingNextPage,
  hasNextPage,
  totalReports,
  selectedReport,
  setSelectedReport,
}) => {
  const mapColorScheme = useMapColorScheme();
  const googleMapId = "4380cd85df35e961";
  const [defaultCenter, setDefaultCenter] =
    useState<google.maps.LatLngLiteral | null>();
  const [defaultZoom, setDefaultZoom] = useState(9);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  useEffect(() => {
    const validCoordinates = reports
      .map((report) => getReportGeometry(report))
      .filter(
        (geometry): geometry is Required<ReportGeometry> =>
          !!geometry &&
          typeof geometry.lat === "number" &&
          typeof geometry.lng === "number",
      );

    if (validCoordinates.length > 0) {
      const latSum = validCoordinates.reduce(
        (sum, coord) => sum + coord.lat,
        0,
      );
      const lngSum = validCoordinates.reduce(
        (sum, coord) => sum + coord.lng,
        0,
      );
      const centerLat = latSum / validCoordinates.length;
      const centerLng = lngSum / validCoordinates.length;

      setDefaultCenter({
        lat: centerLat,
        lng: centerLng,
      } as google.maps.LatLngLiteral);
    }
  }, [reports]);

  const handleMarkerClick = (report: SubmissionSummary, index: number) => {
    setSelectedReport(report);
    reportClickHandler(report, index);
  };

  if (!defaultCenter) {
    return <div>Loading...</div>;
  }

  const selectedGeometry = selectedReport
    ? getReportGeometry(selectedReport)
    : undefined;
  const selectedReportCenter: google.maps.LatLngLiteral | undefined =
    selectedGeometry &&
    typeof selectedGeometry.lat === "number" &&
    typeof selectedGeometry.lng === "number"
      ? { lat: selectedGeometry.lat, lng: selectedGeometry.lng }
      : undefined;

  return (
    <>
      <GoogleMapsProvider>
        <Map
          colorScheme={mapColorScheme}
          defaultCenter={selectedReportCenter ?? defaultCenter}
          defaultZoom={defaultZoom}
          onTilesLoaded={() => setIsMapLoaded(true)}
          mapId={googleMapId}
        >
          {isMapLoaded &&
            reports.map((report, index) => {
              const geometry = getReportGeometry(report);
              const { lat, lng } = geometry || {};

              if (typeof lat === "number" && typeof lng === "number") {
                return (
                  <AdvancedMarker
                    key={index}
                    position={{ lat, lng }}
                    onClick={() => handleMarkerClick(report, index)}
                    clickable
                    zIndex={selectedReport?.id === report.id ? 1000 : 0}
                  >
                    <Pin
                      scale={selectedReport?.id === report.id ? 1.5 : 1}
                      glyphColor={
                        selectedReport?.id === report.id
                          ? colors.primary.contrastText
                          : ""
                      }
                    />
                  </AdvancedMarker>
                );
              }

              return null;
            })}
        </Map>
      </GoogleMapsProvider>

      <div className="loadMoreContainer">
        <div className="report-count">
          Loaded {reports.length} of {totalReports}
        </div>
        <Button
          variant="contained"
          color="primary"
          disabled={isFetchingNextPage || !hasNextPage}
          onClick={loadMore}
          endIcon={isFetchingNextPage ? <CircularProgress size={20} /> : null}
        >
          {isFetchingNextPage ? "Loading..." : "Load More Reports"}
        </Button>
      </div>
    </>
  );
};

export default React.memo(ReportsMap);
